import axios from 'axios';

import {
  ColorCode,
  ColorConfig,
  Feature,
  Heatmap,
} from '@/precision-farming/application-maps/store/baseWorkflowStore/types/Heatmap';
import { Field, Product } from '@/shared/api/rest/models';
import { dataToBase64 } from '@/shared/api/rest/requestUtils';
import { rgbtoHex } from '@/shared/modules/colorHelpers';
import { Variety } from '@/varieties/types';

import { roundToPrecision } from './seedingCommon';
import { SeedingWorkflowConfig } from './seedingWorkflowConfig';

export type WorkflowVariety = {
  id: string | null;
  name: string;
  count?: number;
  fao?: string;
  d?: number;
  deleted?: unknown;
  k?: number;
  kDiff?: number;
  seedRateMax?: number;
  seedRateMin?: number;
  tstamp?: number;
  varietyId?: string;
  seedingMethod?: string;
};

export type Calculation = {
  averageDosage: number | null;
  variety: WorkflowVariety | Variety;
  kDiff: number;
  k: number;
  d: number;
  areaWeightedVarietyMean: number;
  basicSeedStrength: number;
  lossCompensation: number;
  seedrateMin: number;
  seedrateMax: number;
  manualMinSeedrate: number | null;
  manualMaxSeedrate: number | null;
  manualSeedRates: Record<string, number>;
  thousandGrainWeight?: number;
};

export type SeedingZone = {
  varietyValue: number | null;
  seedRate: number | null;
  manualSeedRate: number | null;
  seedRateWithLossCompensation: number | null;
  color: string;
  size: number;
  [key: string]: any;
};

export function getCropName(field: Field, crops: Record<string, Product>): string | null {
  if (!field.cropId) {
    return null;
  }
  return crops[field.cropId]?.name || null;
}

export function calcKDiffFromManualSeedrate(zones: SeedingZone[], calculation: Calculation) {
  const { manualMinSeedrate, manualMaxSeedrate } = calculation;
  const zoneVarietyValues = zones.map((zone) => zone.varietyValue || 0);
  const minVarietyValue = Math.min(...zoneVarietyValues);
  const maxVarietyValue = Math.max(...zoneVarietyValues);
  const kDiff = ((manualMaxSeedrate || 0) - (manualMinSeedrate || 0)) / (maxVarietyValue - minVarietyValue);
  return Number(kDiff.toFixed(2));
}

export function calcBasicSeedStrengthFromManualSeedrate(zones: SeedingZone[], calculation: Calculation) {
  const { manualMinSeedrate, kDiff, areaWeightedVarietyMean } = calculation;
  const minVarietyValue = Math.min(...zones.map((zone) => zone.varietyValue || 0));

  const basicSeedStrength = (manualMinSeedrate || 0) - kDiff * (minVarietyValue - areaWeightedVarietyMean);
  const basicSeedStrengthRounded = roundToPrecision(basicSeedStrength);
  return basicSeedStrengthRounded;
}

export function calcHeatmapColors(heatmaps: Record<string, Heatmap>, zones: SeedingZone[], invertColorScale?: boolean) {
  const newHeatmaps = heatmaps;
  const colorConfig = Object.values(heatmaps)[0].color_config;
  const newColorCodes = recalculateColorCodesForZones(colorConfig, zones, invertColorScale);
  Object.entries(heatmaps).forEach(([heatmapKey, heatmap]) => {
    newHeatmaps[heatmapKey].color_codes = mapColorCodes(heatmap.color_codes, newColorCodes);
    newHeatmaps[heatmapKey].features = mapFeatureFillColor(heatmap.features, newColorCodes);
  });
  return newHeatmaps;
}

function recalculateColorCodesForZones(colorConfig: ColorConfig, zones: SeedingZone[], invertColorScale?: boolean) {
  const valueOfZone = (zone: SeedingZone): number =>
    zone.manualSeedRate ?? zone.seedRateWithLossCompensation ?? zone.seedRate ?? 0;

  const zoneValues = zones.map((zone) => valueOfZone(zone));
  const minValue = Math.min(...zoneValues);
  const maxValue = Math.max(...zoneValues);

  const colorCodes: Record<string, string> = {};
  zones.forEach((zone) => {
    if (!colorCodes[zone.color]) {
      colorCodes[zone.color] = calcZoneColor(colorConfig, minValue, maxValue, valueOfZone(zone), invertColorScale);
    }
  });
  return colorCodes;
}

function calcZoneColor(
  colorConfig: ColorConfig,
  minValue: number,
  maxValue: number,
  seedRate: number,
  invertColorScale?: boolean,
) {
  let value = (seedRate - minValue) / (maxValue - minValue);

  if (invertColorScale) {
    value = 1 - value;
  }

  value = Math.max(0, Math.min(value, 1 - Number.EPSILON));

  const colDelta = 1 / (colorConfig.color_codes.length - 1);
  const cidx0 = Math.trunc(value / colDelta);
  const cidx1 = cidx0 + 1;

  const scale = (value - cidx0 * colDelta) / colDelta;
  const c0 = colorConfig.color_codes[cidx0];
  const c1 = colorConfig.color_codes[cidx1];
  const colRgb = [
    Math.trunc(c0[0] + scale * (c1[0] - c0[0])),
    Math.trunc(c0[1] + scale * (c1[1] - c0[1])),
    Math.trunc(c0[2] + scale * (c1[2] - c0[2])),
  ];
  const colHex = rgbtoHex(colRgb);
  return colHex;
}

function mapColorCodes(oldColorCodes: ColorCode[], newColorCodes: Record<string, string>) {
  return oldColorCodes.map((colorCode) => {
    if (newColorCodes[colorCode.col]) {
      return { ...colorCode, col: newColorCodes[colorCode.col] };
    }
    return colorCode;
  });
}

function mapFeatureFillColor(features: Feature[], newColorCodes: Record<string, string>): Feature[] {
  return features.map((feature) => {
    const oldColor = feature.properties.fill;
    const newColor = newColorCodes[oldColor];
    if (newColor) {
      return {
        ...feature,
        properties: {
          ...feature.properties,
          fill: newColor,
          stroke: newColor,
        },
      };
    }
    return feature;
  });
}

export function getFirstSelectedField(selectedFields: string[], fields: Record<string, Field>) {
  if (selectedFields.length === 0) {
    return null;
  }

  const selectedField = fields[selectedFields[0]];

  return selectedField;
}

export async function findMaterialBasedOnVariety(
  variety: WorkflowVariety,
  selectedField: Field,
  config: SeedingWorkflowConfig,
) {
  if (!variety) {
    return null;
  }

  const {
    processOrder: { companyId },
  } = selectedField; // we assume that all fields have the same companyId

  const filter = [
    'AND',
    ['category.type', '=', 'seed'],
    ['name', '=', variety.name],
    ['unitId', '=', config.getUnitId(variety.seedingMethod)],
    ['OR', ['companyId', 'IS', null], ['companyId', '=', companyId]],
  ];

  try {
    const encodedFilter = dataToBase64(filter);

    const { data } = await axios.get(`/admin/rest/material/?version=2.0&filter=${encodedFilter}`);

    if (data.status === 'success' && data.data.length > 0) {
      return data.data[0];
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function findMaterialBasedOnSelectedFields(
  selectedField: Field,
  variety: WorkflowVariety,
  crops: Record<string, Product>,
  config: SeedingWorkflowConfig,
) {
  const {
    cropId,
    processOrder: { companyId },
  } = selectedField; // we assume that all fields have the same cropId and companyId
  if (!cropId) {
    throw new Error('No cropId found on selected field');
  }
  const material = await findSeedsMaterialBasedOnCropId(cropId, companyId, variety, crops, config);

  return material;
}

async function findSeedsMaterialBasedOnCropId(
  fieldCropId: string,
  fieldCompanyId: string,
  variety: WorkflowVariety,
  crops: Record<string, Product>,
  config: SeedingWorkflowConfig,
) {
  const crop = crops[fieldCropId];

  if (!crop) {
    return false;
  }

  const filter = [
    'AND',
    ['category.type', '=', 'seed'],
    ['mainCropId', '=', fieldCropId],
    ['unitId', '=', config.getUnitId(variety.seedingMethod)],
    ['OR', ['companyId', 'IS', null], ['companyId', '=', fieldCompanyId]],
  ];

  try {
    const encodedFilter = dataToBase64(filter);

    const { data } = await axios.get(`/admin/rest/material/?version=2.0&filter=${encodedFilter}&itemsPerPage=1000`);

    if (data.status === 'success' && data.data.length > 0) {
      const materials = data.data;

      const materialWithMatchingCropName = materials.find((material: any) => material.name.includes(crop?.name));

      return materialWithMatchingCropName || materials[0];
    }

    throw new Error(`No material found: ${JSON.stringify(data)}`);
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function getMostFrequentWorkflowVarietyInFields(
  fields: Field[],
  globalVarieties: Record<string, Variety>,
  workflowVarieties: WorkflowVariety[],
) {
  const fieldVarieties = getWorkflowVarietyCountsInFields(fields, globalVarieties, workflowVarieties);
  if (Object.values(fieldVarieties).length === 0) {
    return null;
  }
  const fieldVarietiesSorted = Object.values(fieldVarieties).sort((a, b) => b.count! - a.count!);
  const mostFrequentVariety = workflowVarieties.find((variety) => variety.name === fieldVarietiesSorted[0].name);
  return mostFrequentVariety;
}

function getWorkflowVarietyCountsInFields(
  fields: Field[],
  globalVarieties: Record<string, Variety>,
  workflowVarieties: WorkflowVariety[],
) {
  const fieldVarieties: Record<string, WorkflowVariety> = {};
  const workflowVarietyNames = workflowVarieties.map((variety) => variety.name);

  Object.values(fields).forEach((field) => {
    if (!field.varietyId) {
      return;
    }

    let varietyName = globalVarieties[field.varietyId].name;

    if (!workflowVarietyNames.includes(varietyName)) {
      const variety = workflowVarieties.find((workflowVariety) => workflowVariety.varietyId === field.varietyId);
      if (!variety) {
        return;
      }
      varietyName = variety.name;
    }

    if (!fieldVarieties[varietyName]) {
      fieldVarieties[varietyName] = {
        id: null,
        name: varietyName,
        count: 1,
      };
      return;
    }
    fieldVarieties[varietyName].count! += 1;
  });
  return fieldVarieties;
}

export function formatVarietyForDropdown(
  variety: WorkflowVariety,
  translateFn: (key: string) => string | undefined,
): { id: string | null; name: string } {
  if (!variety.name || !variety.fao) {
    return variety;
  }
  const name = variety.seedingMethod
    ? `${variety.name} (${variety.fao}) ${translateFn(variety.seedingMethod) ?? variety.seedingMethod}`
    : `${variety.name} (${variety.fao})`;
  return { id: variety.id, name };
}
